import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { notificationsAPI, type EmailPreferences } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'

/**
 * Settings → Email: the weekly digest and milestone emails a subscription
 * sends (backend services/subscriber_emails). The same switch the one-click
 * "Stop these emails" link in those emails turns off.
 */
const SubscriptionEmailsSetting = () => {
  const [prefs, setPrefs] = useState<EmailPreferences | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let live = true
    notificationsAPI
      .getPreferences()
      .then((p) => live && setPrefs(p))
      .catch(() => live && setPrefs(null))
    return () => {
      live = false
    }
  }, [])

  if (!prefs) return null

  const on = prefs.email_subscription_updates
  const blocked = !prefs.email_enabled || !prefs.notifications_enabled

  const toggle = () => {
    setSaving(true)
    notificationsAPI
      .updatePreferences({ email_subscription_updates: !on })
      .then((p) => {
        setPrefs(p)
        toast.success(p.email_subscription_updates ? 'Subscription emails on' : 'Subscription emails off')
      })
      .catch((e) => toast.error(apiErrorMessage(e, 'Could not save your email settings')))
      .finally(() => setSaving(false))
  }

  return (
    <section className="bg-white rounded-lg shadow p-6" aria-labelledby="email-settings-heading" id="email">
      <div className="flex items-center gap-2 mb-4">
        <EnvelopeIcon className="h-5 w-5 text-primary-600" />
        <h2 id="email-settings-heading" className="text-lg font-semibold text-gray-900">
          Email
        </h2>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-900">Subscription emails</p>
          <p className="text-xs text-gray-500">
            A weekly digest of each creator you subscribe to, and a note at 30, 90 and 180 days.
          </p>
          {blocked && (
            <p className="mt-1 text-xs text-amber-700" data-testid="email-blocked">
              All email notifications are off for your account, so none of these are sent.
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="Subscription emails"
          disabled={saving}
          onClick={toggle}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
            on ? 'bg-primary-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              on ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </section>
  )
}

export default SubscriptionEmailsSetting
